import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

function AlertManagerForm() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");

  // State variables
  const [userData, setUserData] = useState([]);
  const [selectedCenterIds, setSelectedCenterIds] = useState(
    savedCenterId || null
  );
  const [notificationSourceData, setNotificationSourceData] = useState([]);
  const [attachmentFileName, setAttachmentFileName] = useState("");
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [roleData, setRoleData] = useState([]);
  const textareaRef = useRef(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const mirrorRef = useRef(null);
  const [isInvalid, setIsInvalid] = useState(false);

  // Form setup
  const {
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    setValue,
    trigger,
    register,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      centerId: "",
      title: "",
      notificationMessage: "",
      datetime: "",
      AttachmentName: "",
      ccTo: "",
      Id: 0,
    },
  });

  // Quill editor modules
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["clean"],
    ],
  };

  const getRoles = async () => {
    if (!token) return;
    try {
      const response = await get("Role", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoleData(response);
    } catch (error) {
      console.error("Error fetching Roles:", error);
    }
  };

  useEffect(() => {
    getRoles();
    fetchNotificationSource();
    getAlertNotificationList();
  }, []);

  useEffect(() => {
    setValue("ccTo", emails.join(", "));
  }, [emails, setValue]);

  // API functions
  const fetchNotificationSource = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=notificationSource`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationSourceData(response.commonSettingDetail);
    } catch (error) {
      console.error("Error fetching notificationSource:", error);
    }
  };

  const getAlertNotificationList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }
    try {
      const response = await get(
        `User/GetAlertNotifications?centerId=${savedCenterId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserData(response);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleEdit = async (row) => {
    try {
      const singleClassData = await get(
        `AlertManager/GetAlertManagerEmailByIdAsync?Id=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const ccEmails = singleClassData.alertNotificationDto.ccTo
        ? singleClassData.alertNotificationDto.ccTo
            .split(",")
            .map((email) => email.trim())
        : [];
      
      // Parse role IDs if they exist in the response
      const roleIds = singleClassData.alertNotificationDto.roleIds || [];
      
      reset({
        Id: singleClassData.alertNotificationDto.id,
        emailTo: singleClassData.alertNotificationDto.emailTo,
        notificationSourceId: singleClassData.alertNotificationDto.notificationSourceId
          ? {
              value: singleClassData.alertNotificationDto.notificationSourceId,
              label: singleClassData.alertNotificationDto.notificationSource,
            }
          : null,
        title: singleClassData.alertNotificationDto.notificationTitle,
        notificationMessage:
          singleClassData.alertNotificationDto.notificationMessage,
      });
      
      setEmails(ccEmails);
      setSelectedRoleIds(roleIds);
      setInputValue("");
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this email?"
    );

    if (confirmDelete) {
      const formData = {
        id: rowId,
        deletedBy: localStorage.getItem("userId"),
      };

      try {
        const response = await post(
          `AlertManager/DeleteAlertManagerEmailById`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.isSuccess) {
          toast.success(response.message || "Success!");
          getAlertNotificationList();
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const handleSelectChange = (e) => {
    const options = e.target.options;
    const values = Array.from(options)
      .filter(option => option.selected)
      .map(option => parseInt(option.value, 10));
    setSelectedRoleIds(values);
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }
    
    const formData = {
      Id: data.Id || 0,
      UserIds: selectedRoleIds, // Send the array of selected role IDs
      notificationSourceId: data.notificationSourceId.value,
      notificationMessage: data.notificationMessage,
      title: data.title,
      attachmentName: attachmentFileName || "",
      base64String: getValues("base64String"),
      createdBy: userId,
      centerId: savedCenterId,
      ccTo: emails.join(", "),
    };

    try {
      const response = await post(
        "AlertManager/SaveAlertManagerEmailForm",
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        getAlertNotificationList();
        reset({
          Id: 0,
          emailTo: "",
          notificationSourceId: null,
          title: "",
          notificationMessage: "",
        });
        setEmails([]);
        setSelectedRoleIds([]);
        setAttachmentFileName("");
      } else {
        toast.error(response.message || "Failed to save data.");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("An error occurred while saving.");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAttachmentFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue("base64String", base64String, { shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  // Table columns
  const columns = [
    { name: "Sr No.", selector: (row, rowIndex) => rowIndex + 1 },
    {
      name: "notificationDate",
      selector: (row) => row.notificationDate,
      omit: true,
    },
    { name: "notificationSource", selector: (row) => row.roleNames },
    { name: "notificationTitle", selector: (row) => row.notificationTitle },
    { name: "notificationMessage", selector: (row) => row.notificationMessage },
    {
      name: "Action",
      width: "80px",
      selector: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleEdit(row)}
          >
            <i className="fas fa-pencil"></i>
          </button>
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleDelete(row.id)}
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Alert Manager Form</h2>
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-md-6">
          <div className="card">
            <input
              {...register("Id")}
              type="hidden"
              className="form-control"
            />
            <div className="card-body">
              <div className="row gy-3 gx-2">
                <div className="col-md-12">
                  <label className="form-label">Roles</label>
                  <select
                    multiple
                    value={selectedRoleIds}
                    className="form-select"
                    onChange={handleSelectChange}
                    style={{ height: 'auto', minHeight: '100px' }}
                  >
                    {roleData.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {/* {selectedRoleIds.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Selected Roles: {selectedRoleIds.map(id => {
                          const role = roleData.find(r => r.id === id);
                          return role ? role.name : '';
                        }).join(', ')}
                      </small>
                    </div>
                  )} */}
                </div>

                {/* <div className="col-md-12">
                  <label className="form-label">CC</label>
                  <div className="email-textarea-container">
                    <div className="email-textarea-wrapper">
                      <div
                        ref={mirrorRef}
                        className="email-mirror"
                        dangerouslySetInnerHTML={formatDisplay()}
                        onClick={handleMirrorClick}
                      />
                      <textarea
                        {...register("ccTo")}
                        ref={textareaRef}
                        className="email-textarea"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onBlur={handleBlur}
                        onScroll={handleScroll}
                      />
                    </div>
                    {isInvalid && (
                      <small className="text-danger">Invalid email address</small>
                    )}
                    <div className="email-counter">
                      {emails.length} {emails.length === 1 ? "email" : "emails"}{" "}
                      entered
                    </div>
                  </div>
                </div> */}

                <div className="col-md-6">
                  <label className="form-label">
                    Source<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="notificationSourceId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={notificationSourceData.map((source) => ({
                          value: source.id,
                          label: source.key,
                        }))}
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption);
                          trigger("notificationSourceId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.notificationSourceId && (
                    <small className="text-danger">
                      {errors.notificationSourceId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Subject<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("title", {
                      required: "This field is required.",
                    })}
                    type="text"
                    className="form-control"
                  />
                  {errors.title && (
                    <small className="text-danger">
                      {errors.title.message}
                    </small>
                  )}
                </div>

                <div className="col-md-12">
                  <label className="form-label">Attachment</label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                  />
                  {/* {attachmentFileName && (
                    <small className="text-muted">
                      Selected file: {attachmentFileName}
                    </small>
                  )} */}
                </div>

                <div className="col-md-12">
                  <label className="form-label">
                    Email Template<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="notificationMessage"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <ReactQuill
                        theme="snow"
                        modules={modules}
                        value={field.value}
                        onChange={field.onChange}
                        className="bg-white"
                      />
                    )}
                  />
                  {errors.notificationMessage && (
                    <small className="text-danger">
                      {errors.notificationMessage.message}
                    </small>
                  )}
                </div>

                <div className="col-md-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">Notification List</label>
                  <DataTable
                    columns={columns}
                    data={userData}
                    pagination
                    dense
                    customStyles={customTableStyles}
                    highlightOnHover={true}
                    pointerOnHover={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default AlertManagerForm;